import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/store";
import { useVault } from "../vault/store";
import { wasm } from "../crypto/core";
import { displayHost, displayUsername, type DecryptedItem, type ItemContent } from "../vault/items";
import { Admin } from "./Admin";
import { desktopAppVersion, launchRdpMstsc, openExternal } from "../desktop/bridge";
import { DownloadApp } from "./DownloadApp";
import { ActionMenu, type MenuAction } from "./Menu";
import { useToast } from "./toast-context";
import { Tooltip } from "./Tooltip";
import { ShortcutsDialog } from "./ShortcutsDialog";
import { SessionTabs } from "./SessionTabs";
import { SessionPlaceholder } from "./SessionPlaceholder";
import { QuickConnect } from "./QuickConnect";
import { CommandPalette, type Command } from "./CommandPalette";
import { VAULT_TAB } from "../vault/sessions";
import { TypeIcon, NodeIcon, Icons } from "./icons";

// Lazy so xterm + the terminal only load on desktop (never in the pure-web bundle).
const SshTerminal = lazy(() => import("../desktop/Terminal").then((m) => ({ default: m.SshTerminal })));
const RdpClient = lazy(() => import("../desktop/RdpClient").then((m) => ({ default: m.RdpClient })));
// Web-native Lane B (jump server) panes — clientless over a broker WebSocket. Lazy so xterm/canvas
// transport only loads when a brokered session is actually opened.
const BrokeredSsh = lazy(() => import("../broker/BrokeredSsh").then((m) => ({ default: m.BrokeredSsh })));
const BrokeredRdp = lazy(() => import("../broker/BrokeredRdp").then((m) => ({ default: m.BrokeredRdp })));
const BrokeredWeb = lazy(() => import("../broker/BrokeredWeb").then((m) => ({ default: m.BrokeredWeb })));
import { useIdleLock } from "./useIdleLock";
import { DraftRestoreBanner } from "./DraftRestoreBanner";
import { useDraft } from "../vault/draft";
import { getUiPrefs, setUiPrefs, getSecurityPrefs, setSecurityPrefs as persistSecurityPrefs, type SecurityPrefs } from "../vault/prefs";
import { Settings } from "./Settings";
import { copyEphemeral } from "./clipboard";
import { DetailsPane } from "./DetailsPane";
import { DialogHost } from "./DialogHost";
import { CreateOneTimeShareDialog, type OneTimeShareSeed } from "./CreateOneTimeShareDialog";
import { ItemGrid } from "./ItemGrid";
import { ConnectScreen } from "./ConnectScreen";
import { ConnectorsAdmin } from "./ConnectorsAdmin";
import { NavigatorTree } from "./NavigatorTree";
import { useVaultWorkspace } from "./useVaultWorkspace";
import { useSessionLaunch } from "./useSessionLaunch";
import { maybeRepairExtension } from "./extension-bridge";
import { useT, useI18n } from "../i18n";

// Build a one-time-share seed from a vault item: its name as the label and every non-empty field as a
// prefilled row (the sender unticks whatever they don't want to send).
function seedFromItem(it: DecryptedItem): OneTimeShareSeed {
  return {
    label: it.content.name,
    fields: it.content.fields.filter((f) => f.value).map((f) => ({ label: f.label, value: f.value })),
  };
}

export function AppShell() {
  const { login, clear } = useAuth();
  const vault = useVault();
  const t = useT();
  const toast = useToast();
  const { lang, setLang } = useI18n();
  // The whole vault-data domain (state, loaders, mutations, derivations) lives in the hook; the
  // flat destructure keeps every name the JSX below has always used.
  const {
    offline, liveOffline, readOnly, health,
    orgs, personalOrg, clientOrgs, selectedOrg, mode, filter, setFilter, orgCaps, orgSettings, setOrgSettings, orgKeyFor,
    items, visible, selected, selectedId, setSelectedId, selectedIds, setSelectedIds, selectItem, clickItem, toggleOne,
    folders, selectedFolderId, setSelectedFolderId, collapsed, toggleCollapse, childrenOf, nodeCount, nodeHidden, folderPath, breadcrumb, selectedNode, deviceCount, folderCount,
    facets, setFacets, updateFacets, searching, typeFacets, tagFacets,
    favorites, setFavorites, recentItems, favoriteItems, showDashboard,
    openOrg, openShared, openTrash, createOrg, restoreFromTrash, reloadItems, moveSelected, deleteFolderUI, bulkDelete, applyBulkEdit, duplicateItem, trashItem, restoreItem,
    templates, openFromTemplate, saveAsTemplate, removeTemplate,
    dropTarget, onItemDragStart, onNodeDragStartTree, onNodeDragOver, onNodeDrop,
    editor, setEditor, importing, setImporting, exporting, setExporting, healthOpen, setHealthOpen, settingsOpen, setSettingsOpen,
    bulkEditing, setBulkEditing, sharing, setSharing, historyId, setHistoryId, recoveryKeyShown, setRecoveryKeyShown,
    movingIds, setMovingIds, newNode, setNewNode, editNode, setEditNode, submitNewNode, submitEditNode,
  } = useVaultWorkspace();
  // Session tabs + item-launch actions (desktop bridge) — same flat-destructure contract.
  const {
    desktop, sessionTabs, activeTab, openSession, closeSession, closeAllSessions, setActiveTab,
    urlOf, canLaunch, openItemSession, nativeFromLaunch, launchLabel, launchItem,
  } = useSessionLaunch(readOnly);
  const [view, setView] = useState<"vault" | "admin" | "connect" | "connectors">("vault");
  // One-time secret share dialog (null = closed). Seed prefills fields from a vault item; {} = blank.
  const [sharingOnce, setSharingOnce] = useState<OneTimeShareSeed | null>(null);
  const [downloadAppOpen, setDownloadAppOpen] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; actions: MenuAction[] } | null>(null);
  const isWindows = typeof navigator !== "undefined" && /Windows/i.test(navigator.userAgent);
  const [navCollapsed, setNavCollapsed] = useState(() => getUiPrefs().navCollapsed);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [appSettingsOpen, setAppSettingsOpen] = useState(false);
  const [appVersion, setAppVersion] = useState("");
  const [securityPrefs, setSecurityPrefs] = useState<SecurityPrefs>(() => getSecurityPrefs());
  const isAdmin = vault.me?.instance_capabilities?.includes("ManageOrganization") ?? false;

  const toggleNav = useCallback(() => {
    setNavCollapsed((c) => {
      const next = !c;
      setUiPrefs({ navCollapsed: next });
      return next;
    });
  }, []);

  // Lock the vault and tear down every live session (idle, manual, or sign-out). Reads the store
  // imperatively: depending on the whole `vault` object made this callback change on every store
  // write (even the org-key cache), which re-registered useIdleLock and reset the idle timer.
  const lockNow = useCallback(() => {
    closeAllSessions();
    useVault.getState().lock();
  }, [closeAllSessions]);

  // Auto-lock the vault after the configured idle timeout / on tab hide (§20). Hiding the tab locks
  // after a grace period (Settings → Security) so switching tabs to look a password up doesn't tear
  // the workspace down; any in-progress item edit survives a lock either way (vault/draft.ts).
  useIdleLock(securityPrefs.idleLockMin * 60_000, lockNow, securityPrefs.hideLockGraceSec * 1000);

  // The running desktop binary's version (empty on web) — shown in Settings → About so a
  // version/UI mismatch (e.g. after a stale-cache update) is visible.
  useEffect(() => {
    desktopAppVersion().then(setAppVersion).catch(() => undefined);
  }, []);

  // Silently re-pair a previously-connected browser extension on load (sessions expire daily;
  // this reconnects SSO users without a click). No-op off Chromium / when never paired.
  useEffect(() => {
    if (desktop || !login) return;
    void maybeRepairExtension(login);
  }, [desktop, login]);

  const cryptoVer = (() => {
    try {
      return wasm.cryptoFormatVersion();
    } catch {
      return 0;
    }
  })();

  // Tree "+" / right-click actions for a node (org root = null parent).
  function nodeCreateActions(parentId: string | null): MenuAction[] {
    return [
      {
        label: t("New item here"),
        onClick: () => {
          setSelectedFolderId(parentId);
          setEditor({ mode: "new" });
        },
      },
      { label: t("New folder here"), onClick: () => setNewNode({ kind: "folder", parentId }) },
      { label: t("New device here"), onClick: () => setNewNode({ kind: "device", parentId }) },
      ...templates.map((tmpl) => ({ label: `📐 ${tmpl.name}`, onClick: () => openFromTemplate(tmpl, parentId) })),
    ];
  }

  // --- Per-item actions (used by row hover, right-click, and the details pane) ---

  /** Full action list for an item, honoring mode/desktop/Windows. */
  function itemActions(it: DecryptedItem): MenuAction[] {
    const a: MenuAction[] = [];
    if (it.type === "ssh") a.push({ label: "▶ " + t("Launch SSH"), onClick: () => openItemSession(it) });
    if (it.type === "rdp") {
      a.push({ label: "▶ " + t("Launch RDP"), onClick: () => openItemSession(it) });
      if (desktop && isWindows && !readOnly)
        a.push({
          label: t("Open in mstsc"),
          onClick: () =>
            void launchRdpMstsc(it.id).catch((e) => window.alert(`${t("RDP launch failed")}: ${e instanceof Error ? e.message : String(e)}`)),
        });
    }
    if (urlOf(it)) a.push({ label: "▶ " + t("Open"), onClick: () => void openExternal(urlOf(it)!) });
    // Offline / connectivity-lost: read-only — only launch/open/copy, no mutations.
    if (readOnly) return a;
    if (mode === "trash") {
      a.push({ label: t("Restore from trash"), onClick: () => void restoreItem(it) });
      return a;
    }
    if (mode !== "shared") a.push({ label: t("Edit"), onClick: () => setEditor({ mode: "edit", itemId: it.id }) });
    if (mode === "org") {
      a.push({ label: t("Duplicate"), onClick: () => void duplicateItem(it) });
      a.push({ label: t("Save as template"), onClick: () => void saveAsTemplate(it) });
      a.push({ label: t("Move to…"), onClick: () => setMovingIds(new Set([it.id])) });
      a.push({ label: t("Share"), onClick: () => setSharing(it.id) });
      a.push({ label: t("Share once…"), onClick: () => setSharingOnce(seedFromItem(it)) });
      a.push({ label: t("History"), onClick: () => setHistoryId(it.id) });
    }
    if (mode !== "shared") a.push({ label: t("Delete"), danger: true, onClick: () => void trashItem(it) });
    return a;
  }

  // Keyboard shortcuts (§20). Catalog lives in ui/shortcuts.ts (shown by the ? help dialog).
  const searchRef = useRef<HTMLInputElement>(null);
  // Any modal layer that must swallow the global single-letter shortcuts.
  const dialogOpen =
    editor !== null || sharing !== null || historyId !== null || importing || exporting ||
    newNode !== null || editNode !== null || movingIds !== null || settingsOpen || bulkEditing ||
    healthOpen || recoveryKeyShown !== null || sharingOnce !== null || shortcutsOpen ||
    appSettingsOpen || downloadAppOpen || paletteOpen || menu !== null;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Command palette (Ctrl/Cmd+K) works everywhere, including from inputs.
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      // Never let single-letter shortcuts fire out of an open dialog: focus sitting on a <button>
      // inside the item editor used to let `n` flip an open edit into a new item, `/` steal focus to
      // the search box behind the modal, and `?`/`[` stack another layer on top.
      if (dialogOpen) return;
      const el = e.target as HTMLElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return;
      if (el && (el.isContentEditable || el.closest('[role="dialog"]'))) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "?") {
        setShortcutsOpen(true);
      } else if (e.key === "[") {
        toggleNav();
      } else if (e.key === "n" && selectedOrg && mode === "org" && !readOnly) {
        setEditor({ mode: "new" });
      } else if ((e.key === "u" || e.key === "p") && selected) {
        const key = e.key === "u" ? "username" : "password";
        const f = selected.content.fields.find((x) => x.key === key);
        if (f?.value) {
          copyEphemeral(f.value);
          toast.success(`${t(key === "username" ? "Username" : "Password")} — ${t("copied")}`);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, selectedOrg, mode, toggleNav, toast, t, dialogOpen, setEditor]);

  async function signOut() {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    closeAllSessions();
    useDraft.getState().wipe(); // scrub any in-progress item edit (§8) — this is a real session end
    vault.reset();
    clear();
  }

  // Reopen an item edit that was torn down with unsaved content (see DraftRestoreBanner). The vault
  // and folder are switched back first so the restored item lands where the user started it.
  async function restoreDraft() {
    const d = useDraft.getState().peek();
    if (!d) return;
    setActiveTab(VAULT_TAB);
    const org = orgs.find((o) => o.id === d.orgId);
    if (org && selectedOrg?.id !== d.orgId) await openOrg(org);
    if (!d.itemId) setSelectedFolderId(d.folderId);
    setEditor(d.itemId ? { mode: "edit", itemId: d.itemId } : { mode: "new", restore: true });
  }

  // Flat command list for the palette (Ctrl/Cmd+K): current-vault items, actions, and navigation.
  // Built lazily (only when the palette is open) so the per-item mapping isn't paid every render.
  function buildCommands(): Command[] {
    const cmds: Command[] = [];
    const goVault = () => setActiveTab(VAULT_TAB);
    for (const it of items) {
      const verb = it.type === "ssh" || it.type === "rdp" ? t("Connect") : urlOf(it) ? t("Open") : t("Show");
      cmds.push({
        id: `item:${it.id}`,
        group: t("Item"),
        icon: <TypeIcon type={it.type} className="h-3.5 w-3.5" />,
        title: `${verb} ${it.content.name}`,
        subtitle: displayHost(it.content) || undefined,
        keywords: `${displayUsername(it.content)} ${(it.content.tags ?? []).join(" ")}`,
        run: () => {
          goVault();
          if (!launchItem(it)) selectItem(it.id);
        },
      });
    }
    const ic = (I: typeof Icons.plus) => <I className="h-3.5 w-3.5" />;
    if (selectedOrg && mode === "org") cmds.push({ id: "act:new", group: t("Action"), icon: ic(Icons.plus), title: t("New item"), run: () => { goVault(); setEditor({ mode: "new" }); } });
    if (selectedOrg) {
      cmds.push({ id: "act:import", group: t("Action"), icon: ic(Icons.download), title: t("Import"), run: () => { goVault(); setImporting(true); } });
      if (orgCaps.has("ExportItems")) cmds.push({ id: "act:export", group: t("Action"), icon: ic(Icons.share), title: t("Export"), run: () => { goVault(); setExporting(true); } });
      cmds.push({ id: "act:trash", group: t("Action"), icon: ic(Icons.trash), title: t("Trash"), run: () => { goVault(); void openTrash(selectedOrg); } });
      cmds.push({ id: "act:health", group: t("Action"), icon: ic(Icons.shield), title: t("Health"), run: () => { goVault(); setHealthOpen(true); } });
    }
    cmds.push({ id: "act:share-once", group: t("Action"), icon: ic(Icons.share), title: t("New one-time share"), run: () => { goVault(); setSharingOnce({}); } });
    cmds.push({ id: "act:sidebar", group: t("Action"), icon: ic(Icons.panelLeft), title: t("Toggle sidebar"), run: toggleNav });
    cmds.push({ id: "act:settings", group: t("Action"), icon: ic(Icons.settings), title: t("Settings"), run: () => setAppSettingsOpen(true) });
    cmds.push({ id: "act:shortcuts", group: t("Action"), icon: ic(Icons.help), title: t("Keyboard shortcuts"), run: () => setShortcutsOpen(true) });
    cmds.push({ id: "act:lock", group: t("Action"), icon: ic(Icons.lock), title: t("Lock"), run: lockNow });
    cmds.push({ id: "act:signout", group: t("Action"), icon: ic(Icons.logout), title: t("Sign out"), run: () => void signOut() });
    for (const o of orgs) cmds.push({ id: `org:${o.id}`, group: t("Go to"), icon: ic(o.is_personal ? Icons.vaultPersonal : Icons.vaultOrg), title: o.is_personal ? t("Personal vault") : o.name, run: () => { goVault(); void openOrg(o); } });
    cmds.push({ id: "nav:shared", group: t("Go to"), icon: ic(Icons.shared), title: t("Shared with me"), run: () => { goVault(); void openShared(); } });
    if (selectedOrg && mode === "org") for (const f of folders) cmds.push({ id: `folder:${f.id}`, group: t("Go to"), icon: <NodeIcon node={f} className="h-3.5 w-3.5" />, title: f.name, subtitle: folderPath(f.parentId) || undefined, run: () => { goVault(); setSelectedFolderId(f.id); } });
    return cmds;
  }

  // Per-vault menu (moved off the top toolbar). Capability-gated where it was before.
  const vaultMenu: MenuAction[] = selectedOrg
    ? [
        { label: t("Import"), onClick: () => setImporting(true) },
        ...(orgCaps.has("ExportItems") ? [{ label: t("Export"), onClick: () => setExporting(true) }] : []),
        { label: t("Trash"), onClick: () => openTrash(selectedOrg) },
        { label: t("Health"), onClick: () => setHealthOpen(true) },
        ...(orgCaps.has("ManageMembers") ? [{ label: t("Settings"), onClick: () => setSettingsOpen(true) }] : []),
        ...templates.map((tmpl) => ({ label: `📐 ${t("Delete template")}: ${tmpl.name}`, danger: true, onClick: () => void removeTemplate(tmpl) })),
      ]
    : [];


  return (
    <div className="flex h-full flex-col bg-bg text-fg">
      {offline ? (
        <div className="flex items-center gap-2 border-b border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs text-amber-300">
          <span>📴</span>
          <span>{t("Offline — read only. Showing your cached items; launch uses saved credentials.")}</span>
        </div>
      ) : liveOffline ? (
        <div className="flex items-center gap-2 border-b border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs text-amber-300">
          <span>📴</span>
          <span>{t("Connection lost — working from memory. Changes are disabled until you reconnect.")}</span>
        </div>
      ) : null}
      <DraftRestoreBanner onRestore={() => void restoreDraft()} />
      <header className="flex items-center gap-3 border-b border-border bg-panel px-3 py-1.5">
        <Tooltip label={navCollapsed ? `${t("Show sidebar")} ([)` : `${t("Hide sidebar")} ([)`}>
          <button
            onClick={toggleNav}
            className="rounded px-1.5 text-muted hover:bg-panel2 hover:text-fg"
            aria-label={navCollapsed ? t("Show sidebar") : t("Hide sidebar")}
          >
            <Icons.panelLeft className="h-4 w-4" />
          </button>
        </Tooltip>
        <div className="flex items-center gap-2 font-semibold">
          {orgSettings?.branding_logo ? (
            <img src={orgSettings.branding_logo} alt="" className="h-5 w-5 rounded object-contain" />
          ) : (
            <span className="grid h-5 w-5 place-items-center rounded bg-accent text-white">
              <Icons.shield className="h-3.5 w-3.5" />
            </span>
          )}
          <span className="tracking-tight">{orgSettings?.branding_name || "Vault"}</span>
        </div>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted">
          {!desktop && (
            <Tooltip label={t("Download desktop app")}>
              <button
                onClick={() => setDownloadAppOpen(true)}
                className="rounded border border-border px-2 py-1 hover:border-accent"
              >
                {t("Download app")}
              </button>
            </Tooltip>
          )}
          <Tooltip label={t("Vault crypto runs in your browser")}>
            <span>crypto v{cryptoVer}</span>
          </Tooltip>
          <button
            onClick={() => setView((v) => (v === "connect" ? "vault" : "connect"))}
            className={`rounded border px-2 py-1 ${view === "connect" ? "border-accent text-fg" : "border-border hover:border-accent"}`}
          >
            {view === "connect" ? t("Vault") : t("Connect")}
          </button>
          {isAdmin && (
            <button
              onClick={() => setView((v) => (v === "connectors" ? "vault" : "connectors"))}
              className={`rounded border px-2 py-1 ${view === "connectors" ? "border-accent text-fg" : "border-border hover:border-accent"}`}
            >
              {view === "connectors" ? t("Vault") : t("Connectors")}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setView((v) => (v === "admin" ? "vault" : "admin"))}
              className={`rounded border px-2 py-1 ${view === "admin" ? "border-accent text-fg" : "border-border hover:border-accent"}`}
            >
              {view === "admin" ? t("Vault") : t("Admin")}
            </button>
          )}
          <Tooltip label={t("Switch language")}>
            <button
              onClick={() => setLang(lang === "cs" ? "en" : "cs")}
              className="rounded border border-border px-2 py-1 hover:border-accent"
            >
              {lang === "cs" ? "CS" : "EN"}
            </button>
          </Tooltip>
          <Tooltip label={`${t("Keyboard shortcuts")} (?)`}>
            <button
              onClick={() => setShortcutsOpen(true)}
              className="rounded border border-border px-2 py-1 hover:border-accent"
              aria-label={t("Keyboard shortcuts")}
            >
              <Icons.help className="h-4 w-4" />
            </button>
          </Tooltip>
          <Tooltip label={t("Settings")}>
            <button
              onClick={() => setAppSettingsOpen(true)}
              className="rounded border border-border px-2 py-1 hover:border-accent"
              aria-label={t("Settings")}
            >
              <Icons.settings className="h-4 w-4" />
            </button>
          </Tooltip>
          <span className="flex items-center gap-1.5 font-mono text-[11px]"><Icons.vaultPersonal className="h-3.5 w-3.5" /> {login}</span>
          <Tooltip label={t("Lock")}>
            <button onClick={lockNow} aria-label={t("Lock")} className="rounded border border-border px-2 py-1 hover:border-accent">
              <Icons.lock className="h-4 w-4" />
            </button>
          </Tooltip>
          <Tooltip label={t("Sign out")}>
            <button onClick={signOut} aria-label={t("Sign out")} className="rounded border border-border px-2 py-1 hover:border-accent">
              <Icons.logout className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </header>

      <SessionTabs />

      <div className="relative min-h-0 flex-1">
      {/* Vault home tab — 3-pane workspace (or admin). Always mounted so sessions persist. */}
      <div className={activeTab !== VAULT_TAB ? "hidden" : "absolute inset-0 flex min-h-0 flex-col"}>
      {desktop && view === "vault" && <QuickConnect onOpen={openSession} />}
      {view === "admin" ? (
        <Admin onClose={() => setView("vault")} />
      ) : view === "connect" ? (
        <ConnectScreen
          orgs={orgs}
          selectedOrg={selectedOrg}
          openOrg={openOrg}
          items={items}
          launchItem={launchItem}
        />
      ) : view === "connectors" ? (
        <ConnectorsAdmin orgs={orgs} selectedOrg={selectedOrg} openOrg={openOrg} />
      ) : (
      <div className="flex min-h-0 flex-1">
        {/* Navigator */}
        {!navCollapsed && (
        <NavigatorTree
          personalOrg={personalOrg}
          clientOrgs={clientOrgs}
          selectedOrg={selectedOrg}
          mode={mode}
          readOnly={readOnly}
          items={items}
          folders={folders}
          selectedFolderId={selectedFolderId}
          setSelectedFolderId={setSelectedFolderId}
          collapsed={collapsed}
          toggleCollapse={toggleCollapse}
          childrenOf={childrenOf}
          nodeCount={nodeCount}
          nodeHidden={nodeHidden}
          dropTarget={dropTarget}
          onNodeDragOver={onNodeDragOver}
          onNodeDrop={onNodeDrop}
          onNodeDragStartTree={onNodeDragStartTree}
          openOrg={openOrg}
          openShared={openShared}
          createOrg={createOrg}
          deleteFolderUI={deleteFolderUI}
          setEditNode={setEditNode}
          setMenu={setMenu}
          nodeCreateActions={nodeCreateActions}
        />
        )}

        <ItemGrid
          searchRef={searchRef}
          facets={facets}
          setFacets={setFacets}
          updateFacets={updateFacets}
          typeFacets={typeFacets}
          tagFacets={tagFacets}
          filter={filter}
          setFilter={setFilter}
          mode={mode}
          readOnly={readOnly}
          searching={searching}
          selectedOrg={selectedOrg}
          orgs={orgs}
          items={items}
          visible={visible}
          selectedId={selectedId}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          favorites={favorites}
          selectedFolderId={selectedFolderId}
          setSelectedFolderId={setSelectedFolderId}
          breadcrumb={breadcrumb}
          folderPath={folderPath}
          selectedNode={selectedNode}
          setEditNode={setEditNode}
          nodeCreateActions={nodeCreateActions}
          vaultMenu={vaultMenu}
          itemActions={itemActions}
          setMenu={setMenu}
          showDashboard={showDashboard}
          deviceCount={deviceCount}
          folderCount={folderCount}
          recentItems={recentItems}
          favoriteItems={favoriteItems}
          desktop={desktop}
          canLaunch={canLaunch}
          launchItem={launchItem}
          launchLabel={launchLabel}
          selectItem={selectItem}
          clickItem={clickItem}
          toggleOne={toggleOne}
          onItemDragStart={onItemDragStart}
          setEditor={setEditor}
          setImporting={setImporting}
          setHealthOpen={setHealthOpen}
          setBulkEditing={setBulkEditing}
          setMovingIds={setMovingIds}
          bulkDelete={bulkDelete}
        />

        <DetailsPane
          selected={selected}
          mode={mode}
          readOnly={readOnly}
          selectedOrg={selectedOrg}
          orgKeyFor={orgKeyFor}
          folders={folders}
          favorites={favorites}
          setFavorites={setFavorites}
          breakGlass={vault.me?.is_break_glass ?? false}
          canLaunch={canLaunch}
          launchItem={launchItem}
          launchLabel={launchLabel}
          setEditor={setEditor}
          duplicateItem={duplicateItem}
          trashItem={trashItem}
          restoreFromTrash={restoreFromTrash}
          setSharing={setSharing}
          shareOnce={(it) => setSharingOnce(seedFromItem(it))}
          setHistoryId={setHistoryId}
          moveSelected={moveSelected}
        />
      </div>
      )}
      </div>
      {/* Live session tabs — mounted while open (hidden when inactive) so they survive switching. */}
      {sessionTabs.map((tab) => (
        <div key={tab.key} className={activeTab !== tab.key ? "hidden" : "absolute inset-0"}>
          {tab.kind === "placeholder" ? (
            <SessionPlaceholder itemType={tab.itemType ?? "ssh"} onDownload={() => setDownloadAppOpen(true)} />
          ) : tab.kind === "brokered-ssh" ? (
            <Suspense fallback={null}>
              <BrokeredSsh
                itemId={(tab.launch as { itemId: string }).itemId}
                title={tab.title}
                onClose={() => closeSession(tab.key)}
                connectorId={tab.connectorId}
              />
            </Suspense>
          ) : tab.kind === "brokered-rdp" ? (
            <Suspense fallback={null}>
              <BrokeredRdp
                itemId={(tab.launch as { itemId: string }).itemId}
                title={tab.title}
                onClose={() => closeSession(tab.key)}
                connectorId={tab.connectorId}
              />
            </Suspense>
          ) : tab.kind === "brokered-web" ? (
            <Suspense fallback={null}>
              <BrokeredWeb
                itemId={(tab.launch as { itemId: string }).itemId}
                title={tab.title}
                onClose={() => closeSession(tab.key)}
                connectorId={tab.connectorId}
              />
            </Suspense>
          ) : tab.kind === "rdp" ? (
            <Suspense fallback={null}>
              <RdpClient
                launch={tab.launch!}
                title={tab.title}
                onClose={() => closeSession(tab.key)}
                onFallbackNative={() => nativeFromLaunch(tab.launch!)}
              />
            </Suspense>
          ) : (
            <Suspense fallback={null}>
              <SshTerminal launch={tab.launch!} title={tab.title} onClose={() => closeSession(tab.key)} />
            </Suspense>
          )}
        </div>
      ))}
      </div>

      <footer className="flex items-center gap-4 border-t border-border bg-panel px-3 py-1 text-[11px] text-muted">
        <span className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${readOnly ? "bg-danger" : health === "ok" ? "bg-ok" : "bg-danger"}`} />
          {readOnly ? t("Offline") : health === "ok" ? t("Connected") : t(health)}
        </span>
        <span className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${offline ? "bg-amber-400" : "bg-ok"}`} /> {offline ? t("Read-only") : t("Vault unlocked")}
        </span>
        <span className="ml-auto">{orgs.length} vault(s)</span>
      </footer>

      <DialogHost
        selectedOrg={selectedOrg}
        orgKeyFor={orgKeyFor}
        orgs={orgs}
        items={items}
        folders={folders}
        selectedIds={selectedIds}
        selectedFolderId={selectedFolderId}
        orgSettings={orgSettings}
        setOrgSettings={setOrgSettings}
        reloadItems={reloadItems}
        setSelectedId={setSelectedId}
        sharing={sharing}
        setSharing={setSharing}
        historyId={historyId}
        setHistoryId={setHistoryId}
        importing={importing}
        setImporting={setImporting}
        exporting={exporting}
        setExporting={setExporting}
        newNode={newNode}
        setNewNode={setNewNode}
        submitNewNode={submitNewNode}
        editNode={editNode}
        setEditNode={setEditNode}
        submitEditNode={submitEditNode}
        movingIds={movingIds}
        setMovingIds={setMovingIds}
        settingsOpen={settingsOpen}
        setSettingsOpen={setSettingsOpen}
        recoveryKeyShown={recoveryKeyShown}
        setRecoveryKeyShown={setRecoveryKeyShown}
        healthOpen={healthOpen}
        setHealthOpen={setHealthOpen}
        bulkEditing={bulkEditing}
        setBulkEditing={setBulkEditing}
        applyBulkEdit={applyBulkEdit}
        editor={editor}
        setEditor={setEditor}
      />

      {sharingOnce && <CreateOneTimeShareDialog initial={sharingOnce} onClose={() => setSharingOnce(null)} />}

      {downloadAppOpen && <DownloadApp onClose={() => setDownloadAppOpen(false)} />}

      {menu && <ActionMenu x={menu.x} y={menu.y} actions={menu.actions} onClose={() => setMenu(null)} />}

      {shortcutsOpen && <ShortcutsDialog onClose={() => setShortcutsOpen(false)} />}

      {paletteOpen && <CommandPalette getCommands={buildCommands} onClose={() => setPaletteOpen(false)} />}

      {appSettingsOpen && (
        <Settings
          security={securityPrefs}
          onSaveSecurity={(p) => {
            setSecurityPrefs(persistSecurityPrefs(p));
            toast.success(t("Settings saved"));
          }}
          lang={lang}
          onSetLang={setLang}
          cryptoVer={cryptoVer}
          desktop={desktop}
          appVersion={appVersion}
          login={login ?? ""}
          onClose={() => setAppSettingsOpen(false)}
        />
      )}
    </div>
  );
}

// Re-export so ItemEditor type stays colocated with usage.
export type { ItemContent };
